// A button that asks before it acts. B-77.
//
// The first tap arms it and the second does it. `confirmLabel` is
// deliberately a question and not "Confirm": the row is small and the word
// has to carry the whole question, and the question belongs beside the name
// it is about rather than in a sheet over the top of it.
//
// IT DISARMS ITSELF, and until 2026-09-20 it did not. Its own comment said it
// disarmed "when anything else on the panel happens (the parent remounts it
// with a new key)" — and the parents give the SEAT a key, not the button, so
// React kept the armed state right where it was. An armed "Open it?" stayed
// armed: tap it on one row, change your mind, scroll away, come back later
// and one tap opens somebody's seat. B-77 lists that exact shape under
// *Wrong looks like* — "a button that stays armed after you look away and
// fires later" — so the document was right and the component was not. It had
// no test of any kind, which is how the comment and the code could disagree
// for as long as they did (AUDIT-2026-09-19 F9).
//
// TWO MECHANISMS, BECAUSE ONE IS NOT ENOUGH ON A PHONE. Blur covers a
// keyboard user tabbing away, and it is the obvious one — but Safari does not
// always focus a button on tap, so on the device this rule matters most blur
// may never fire. A capture-phase `pointerdown` anywhere outside the button
// is what actually closes it: touching anything else disarms, which is what
// "anything else on the panel happens" was always supposed to mean.
//
// IT LIVES HERE RATHER THAN IN `LeaguePreDraftScreen` because a test cannot
// import a function that is private to a screen, and this is the one thing
// standing between a thumb and four actions the server performs
// unconditionally and by design.

import { useEffect, useRef, useState } from 'react';
import { Button } from '../../kit/ui';

export function Confirm({
  label,
  confirmLabel,
  onConfirm,
  busy,
  tone = 'bad',
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  busy: boolean;
  /** How the armed press reads: a loss (`bad`), or a caution. */
  tone?: 'bad' | 'warn';
}) {
  const [armed, setArmed] = useState(false);
  // THE SPAN HOLDS THE REF: a press anywhere outside it disarms, and the
  // kit's button is the one thing inside.
  const self = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!armed) return;
    const elsewhere = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && self.current?.contains(target) === true) return;
      setArmed(false);
    };
    // Capture, so a handler that stops propagation cannot leave this armed.
    document.addEventListener('pointerdown', elsewhere, true);
    return () => document.removeEventListener('pointerdown', elsewhere, true);
  }, [armed]);

  return (
    <span ref={self} className="fsp-confirm" onBlur={() => setArmed(false)}>
      <Button
        size="xs"
        variant={armed ? (tone === 'warn' ? 'caution' : 'danger') : 'secondary'}
        disabled={busy}
        onClick={() => {
          if (!armed) {
            setArmed(true);
            return;
          }
          setArmed(false);
          onConfirm();
        }}
      >
        {armed ? confirmLabel : label}
      </Button>
    </span>
  );
}
